import * as React from "react";
import { Headings } from "@arctheme-components/elements/Headings/Headings";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";

export const CollectionHeader = ({ settings }) => {
    const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);
    
    // Find background image block if available
    const imageBlock = settings.blocks?.find(block => block.type === 'image');
    
    // Get content alignment values
    const contentAlignmentDesktop = settings.section.content_alignment_desktop || 'center';
    const contentAlignmentMobile = settings.section.content_alignment_mobile || 'center';
    
    // Get correct heading type based on device
    const headingType = isMobile 
        ? settings.section.heading_type_mobile || 'h3' 
        : settings.section.heading_type_desktop || 'h2';
    
    // Get vertical positioning
    const verticalPositionDesktop = settings.section.content_vertical_position_desktop || 'center';
    const verticalPositionMobile = settings.section.content_vertical_position_mobile || 'center';
    
    // Determine padding settings based on padding area
    const paddingArea = settings.section.padding_area || 'outside';
    
    // Create styles for outer and inner containers
    const outerContainerStyles = {
        minHeight: isMobile 
            ? `${settings.section.mobile_min_height || 180}px` 
            : `${settings.section.desktop_min_height || 300}px`,
        ...(paddingArea === 'outside' ? {
            paddingTop: isMobile 
                ? `${settings.section.padding_top_mobile || 0}px` 
                : `${settings.section.padding_top_desktop || 0}px`,
            paddingBottom: isMobile 
                ? `${settings.section.padding_bottom_mobile || 0}px` 
                : `${settings.section.padding_bottom_desktop || 0}px`,
            paddingLeft: isMobile 
                ? `${settings.section.padding_left_right_mobile || 0}px` 
                : `${settings.section.padding_left_right_desktop || 0}px`,
            paddingRight: isMobile 
                ? `${settings.section.padding_left_right_mobile || 0}px` 
                : `${settings.section.padding_left_right_desktop || 0}px`
        } : {})
    };
    
    const innerContainerStyles = {
        ...(paddingArea === 'inside' ? {
            paddingTop: isMobile 
                ? `${settings.section.padding_top_mobile || 0}px` 
                : `${settings.section.padding_top_desktop || 0}px`,
            paddingBottom: isMobile 
                ? `${settings.section.padding_bottom_mobile || 0}px` 
                : `${settings.section.padding_bottom_desktop || 0}px`,
            paddingLeft: isMobile 
                ? `${settings.section.padding_left_right_mobile || 0}px` 
                : `${settings.section.padding_left_right_desktop || 0}px`,
            paddingRight: isMobile 
                ? `${settings.section.padding_left_right_mobile || 0}px` 
                : `${settings.section.padding_left_right_desktop || 0}px`
        } : {})
    };

    if (settings.section.layout_type === 'image') {
        return (
            <div 
                className="collection-header__with-image relative w-full h-full" 
                style={outerContainerStyles}
            >
                {/* Background Image */}
                {imageBlock && 
                    <div className={`collection-header__background absolute z-0 inset-0 w-full h-full overflow-hidden ${settings.section.darken_image && "content-over-media"}`}>
                        <picture className="w-full h-full block">
                            <source
                                media="(max-width: 767px)"
                                srcSet={imageBlock.settings.mobile_image || imageBlock.settings.image}
                            />
                            <img
                                src={imageBlock.settings.image}
                                className="w-full h-full object-cover"
                                style={{ objectPosition: imageBlock.settings.image_position || 'center center' }}
                                alt=""
                            />
                        </picture>
                        
                        {/* Overlay if specified */}
                        {imageBlock.settings.overlay_opacity > 0 && (
                            <div 
                                className="absolute inset-0 w-full h-full" 
                                style={{
                                    backgroundColor: imageBlock.settings.overlay_color || '#000000',
                                    opacity: imageBlock.settings.overlay_opacity / 100
                                }}
                            />
                        )}
                    </div>
                }
                
                {/* Content Container */}
                <div 
                    className={`
                        absolute top-0 left-0 w-full flex flex-col h-full
                        text-${contentAlignmentMobile} lg:text-${contentAlignmentDesktop}
                        justify-${verticalPositionMobile} lg:justify-${verticalPositionDesktop}
                        relative z-2
                    `}
                    style={innerContainerStyles}
                >
                    <div className={`mb-[${settings.section.title_margin_bottom_mobile}px] lg:mb-[${settings.section.title_margin_bottom_desktop}px]`}>
                        <Headings
                            text={settings.section.title}
                            type={headingType}
                            classes={`collection-header__with-image__title`}
                        />
                              {settings.section.show_description && settings.description && (
                        <div 
                            className={`mx-auto text-${contentAlignmentMobile} lg:text-${contentAlignmentDesktop}`} 
                     
                        >
                            <Paragraph
                                text={settings.description}
                                type={settings.section.body_type || 'b2'}
                            />
                        </div>
                    )}
                    </div>
                    
              
                </div>
            </div>
        )
    } else {
        return (
            <div 
                className={`
                    collection-header-without-image flex flex-col h-full
                    text-${contentAlignmentMobile} lg:text-${contentAlignmentDesktop}
                    justify-${verticalPositionMobile} lg:justify-${verticalPositionDesktop}
                `}
                style={outerContainerStyles}
            >
                <div 
                    className="w-full flex flex-col h-full"
                    style={innerContainerStyles}
                >
                    <div className={`mb-[${settings.section.title_margin_bottom_mobile}px] lg:mb-[${settings.section.title_margin_bottom_desktop}px]`}>
                        <Headings
                            text={settings.section.title}
                            type={headingType}
                            classes={`collection-header-without-image__title`}
                        />
                    </div>
                    
                    {settings.section.show_description && settings.description && (
                        <div 
                            className={`mx-auto text-${contentAlignmentMobile} lg:text-${contentAlignmentDesktop}`} 
                            style={{maxWidth: settings.section.content_width || '600px'}}
                        >
                            <Paragraph
                                text={settings.description}
                                type={settings.section.body_type || 'b2'}
                            />
                        </div>
                    )}
                </div>
            </div>
        )
    }
};

export default CollectionHeader;